import React from 'react';
import { createRoot } from 'react-dom/client';
import { IWidgetInitVariables } from 'types/form';
import { PRIMARY_SOURCE } from '@shared/constants/utm';
import Widget from './components/Widget';

interface IelementId {
  widgetRenderTargetId: string;
}
const isWidget = process.env.REACT_APP_IS_WIDGET;
let init;

if (isWidget === 'true') {
  init = function (elementId: IelementId, initVariables?: IWidgetInitVariables) {
    const targetElement = document.getElementById(elementId?.widgetRenderTargetId);

    if (targetElement) {
      // Create a wrapper for CDX host the shadow DOM
      const shadowHost = document.createElement('div');
      shadowHost.id = 'CDX-widget-shadow-host';
      targetElement.appendChild(shadowHost);

      // Create shadow root
      const shadowRoot = shadowHost.attachShadow({ mode: 'open' });

      // Create container for CDX widget
      const container = document.createElement('div');
      container.id = 'cdx-shadow-root-container';
      shadowRoot.appendChild(container);

      const styleElement = document.createElement('style');

      // Prepare CSS variables declaration with smart overriding
      let cssVarsDeclaration = ':host {\n';

      const initStyleOverrides = {
        '--primary-color': `${initVariables?.branding?.primaryColor || '#00aa8f'};`,
        '--secondary-color': `${initVariables?.branding?.secondaryColor || '#7b1b17'};`,
      };
      // Merge provided variables with defaults
      const mergedVariables = { ...initStyleOverrides };

      // Add variables with fallback to existing values
      for (const [name, value] of Object.entries(mergedVariables)) {
        const varName = name.startsWith('--') ? name : `--${name}`;
        // Use var() with fallback to allow existing variables to persist if not explicitly overridden
        cssVarsDeclaration += `  ${varName}:  ${value}\n`;
      }

      cssVarsDeclaration += '}\n\n';

      //  eslint-disable-next-line @typescript-eslint/no-var-requires
      const componentStyles = require('./index.css').default || require('./index.css');

      // Combine variable declarations with component styles
      styleElement.textContent = cssVarsDeclaration + componentStyles;

      shadowRoot.appendChild(styleElement);

      //  Add Google Fonts font Links directly to DOM header

      const fontLink1 = document.createElement('link');
      fontLink1.rel = 'preconnect';
      fontLink1.href = 'https://fonts.googleapis.com';

      const fontLink2 = document.createElement('link');
      fontLink2.rel = 'preconnect';
      fontLink2.href = 'https://fonts.gstatic.com';
      fontLink2.crossOrigin = 'anonymous';

      const fontLink3 = document.createElement('link');
      fontLink3.id = 'cdx-google-fonts';
      fontLink3.rel = 'stylesheet';
      fontLink3.href =
        'https://fonts.googleapis.com/css2?family=Advent+Pro:ital,wght@0,100..900;1,100..900&family=Gothic+A1&family=Open+Sans:ital,wght@0,300..800;1,300..800&display=swap';

      document.head.appendChild(fontLink1);
      document.head.appendChild(fontLink2);
      document.head.appendChild(fontLink3);

      const renderWidget = () => {
        const root = createRoot(container);
        root.render(<Widget initValues={initVariables} />);
      };

      const recaptchaScript = document.createElement('script');
      recaptchaScript.src = `https://www.google.com/recaptcha/api.js?render=${process.env.REACT_APP_RECAPTCHA_KEY}`;
      recaptchaScript.async = true;

      recaptchaScript.onload = () => {
        window.grecaptcha.ready(() => {
          renderWidget();
        });
      };

      document.body.appendChild(recaptchaScript);
    } else {
      console.error('Target element not found.');
    }
  };
} else {
  require('./index.css');
  const root = createRoot(document.getElementById('root') as HTMLElement);

  const captureUTMParameters = (): Record<string, string> => {
    const utmData: Record<string, string> = {};
    const params = new URLSearchParams(window.location.search);
    const keys: string[] = [
      'utm_medium',
      'utm_campaign',
      'utm_content',
      'secondary_source',
      'gclid',
      'subid',
      'clickref',
      'search_id',
      'subid1',
      'subid2',
      'subid3', // This will be populated with the device identifier.
      'sharedid',
      'clickid',
      'arm_enabled',
      'utm_source',
      'irclickid',
      'loan_application_id',
      'session_id',
      'anonymous_id',
      'referrer',
      'timestamp',
      'widget_type',
      'widget_location',
      'page_url',
    ];

    keys.forEach((key: string) => {
      const value = params.get(key);
      if (value) {
        utmData[key] = value;
      }
    });

    return utmData;
  };

  const extraDataObj = {
    ...captureUTMParameters(),
  };

  const initValues = {
    PartnerName: PRIMARY_SOURCE,
    tracking: {
      // Pass extra tracking data as a stringified JSON object.
      extraData: JSON.stringify(extraDataObj),
    },
  };
  root.render(
    // <React.StrictMode>
      <Widget initValues={initValues} />
    // </React.StrictMode>
  );
}

export default init;
